import { Action, createReducer, on } from '@ngrx/store';
import { LoadStatus } from '../../load-status';
import {
    addStatusToNotificationEntry,
    cancellationConfirmationRemoved,
    cancellationConfirmationSaved,
    initNotification,
    legacyNotificationCompletionsMigrated,
    markNotificationCompleted,
    notificationCancelled,
    notificationFailed,
    notificationLoaded,
    notificationRestoredFromStorage,
    notificationStorageSyncError,
    removeExpiredCompletedNotifications,
    setCurrentNotification,
    setIncorrectNotifications,
    setNotificationCompletions,
    setNotificationEntries,
    setNotificationStatusFields,
    travelStatusRecorded,
    updateNotificationInternalInfoLines,
    updateNotificationStatusFields,
} from '../actions/notification.actions';
import {
    carryOverStatusFields,
    completionKeyOf,
    liftEmbeddedCompletions,
    withEntryDefaults,
} from '../notification-collection.converters';
import {
    COMPLETION_RETENTION_MS,
    initialNotificationCollectionState,
    NotificationCollectionState,
    NotificationCompletionsState,
    NotificationCompletionState,
    NotificationState,
    NotificationStatusModelFields,
    NotificationStatusRecordState,
} from '../notification-collection.state';
import { NotificationIdState } from '../../utilities/notification-id.type';
import { NotificationStateUtilities } from '../../utilities/notification-state-utlities';
import { NotificationLongTextLineState } from '../notification-entities.state';
import { notificationStartForEntry } from '../selectors/notification.selector';
import { reconcileLoadedStatuses } from './load-reconciliation';
import {
    statusRecordsForTravelStatus,
    TravelStatusSubmission,
    upsertStatusRecords,
} from '../notification-status.rules';

/**
 * The entries ordered by the start the notification is shown with (`notificationStartForEntry`),
 * ascending, so the stored order matches the dashboard's as long as no status write moves a start.
 *
 * @param entries
 */
function sortEntriesByStart(entries: NotificationState[]): NotificationState[] {
    return [...entries].sort((a, b) => notificationStartForEntry(a).valueOf() - notificationStartForEntry(b).valueOf());
}

/**
 * Replaces the entry stored under `id` with the result of `change`, and leaves the state untouched
 * when no entry has that id. Shared by every status write: the status of a notification the store
 * does not hold has nowhere to live now that it sits on the entry, and the next load would discard
 * it anyway, so those writes are dropped rather than creating a notification-less entry.
 *
 * @param state the notification slice
 * @param id the entry to change
 * @param change produces the replacement entry
 * @returns the slice with the entry replaced, or the unchanged slice
 */
function changeEntry(
    state: Readonly<NotificationCollectionState>,
    id: NotificationIdState,
    change: (entry: NotificationState) => NotificationState,
): Readonly<NotificationCollectionState> {
    const index = state.notifications.findIndex((entry) =>
        NotificationStateUtilities.compare(entry.notificationId, id),
    );
    if (index === -1) {
        return state;
    }
    const changed = change(state.notifications[index]);
    if (changed === state.notifications[index]) {
        return state;
    }
    return { ...state, notifications: state.notifications.map((entry, i) => (i === index ? changed : entry)) };
}

/**
 * Applies `change` to every entry. Returns the unchanged slice when nothing came out different, so
 * a write that changes nothing causes neither a re-render nor a persist.
 *
 * @param state the notification slice
 * @param change produces the replacement for one entry, or returns it unchanged
 * @returns the slice with the changed entries, or the unchanged slice
 */
function changeEveryEntry(
    state: Readonly<NotificationCollectionState>,
    change: (entry: NotificationState) => NotificationState,
): Readonly<NotificationCollectionState> {
    const entries = state.notifications.map(change);
    const unchanged = entries.every((entry, index) => entry === state.notifications[index]);
    return unchanged ? state : { ...state, notifications: entries };
}

/**
 * Marks the slice as loading without discarding what is already in it, so a warm cache (from
 * hydration or a previous load) stays visible while the re-fetch is in flight. Set
 * unconditionally on every `initNotification`, including when the slice already holds hydrated
 * notifications — this is the invariant that keeps `init()` re-fetching on every call rather than
 * treating a hydrated slice as a substitute for the fetch. Leaving `lastLoad` alone is what keeps
 * `selectNotificationInitialized` `true` across the re-fetch.
 *
 * @param state the notification slice
 */
export function setNotificationLoadingReducer(
    state: Readonly<NotificationCollectionState>,
): Readonly<NotificationCollectionState> {
    return { ...state, loadStatus: LoadStatus.Loading };
}

/**
 * Turns the freshly-fetched entries into the collection the slice holds, and marks the slice as
 * loaded.
 *
 * The whole merge is owned here rather than by the caller, in the order the steps depend on each
 * other:
 *
 * 1. the recorded status of the notifications that are still there is carried over onto the fresh
 *    payloads ({@link carryOverStatusFields}) — which is also what drops the status of the ones the
 *    backend no longer sends, and what the reconciliation below reads;
 * 2. what the device recorded is reconciled against what the backend now says
 *    ({@link reconcileLoadedStatuses});
 * 3. the collection is ordered by notification start.
 *
 * A notification the backend stopped sending drops out, status and all. Its completion record, if
 * it has one, is unaffected: that lives in `completions`, apart from the entries.
 *
 * Two things the reconciliation decides cannot be carried out from here, since a reducer writes its
 * own slice and nothing else: the timesheet's start of work has to be pulled back to the earliest
 * travel, and the appended statuses have to be logged. Both are recorded on `lastLoad` for the
 * effects that do them — `TimesheetEffects.pullBackStartOfWork$` and
 * `NotificationEffects.logStatusesAddedByLoad$`.
 *
 * `lastLoad` is also how readers of the collection learn that it was refreshed rather than merely
 * changed — see `NotificationCollectionState.lastLoad`. The sequence number is derived from the
 * previous one rather than taken from a clock, so the reducer stays pure.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.entries the freshly-fetched entries
 * @param props.isAppStart whether this load is the one performed during app start
 * @returns the slice with the new collection, reconciled, sorted, loaded, and marked
 */
export function setNotificationLoadedReducer(
    state: Readonly<NotificationCollectionState>,
    props: { entries: NotificationState[]; isAppStart: boolean },
): Readonly<NotificationCollectionState> {
    const carried = carryOverStatusFields(props.entries, state.notifications);
    const { entries, earliestTravel, addedStatuses, statusDiscrepancies } = reconcileLoadedStatuses(
        carried,
        props.isAppStart,
    );

    return {
        ...state,
        notifications: sortEntriesByStart(entries),
        loadStatus: LoadStatus.Loaded,
        error: undefined,
        lastLoad: {
            sequence: (state.lastLoad?.sequence ?? 0) + 1,
            isAppStart: props.isAppStart,
            earliestTravel,
            addedStatuses,
            statusDiscrepancies,
        },
    };
}

/**
 * Advances the cancellation marker, which is how readers learn that the user cancelled a
 * notification — see `NotificationCollectionState.lastCancellation`. Like `setLoadedReducer`'s
 * sequence, the number is derived from the previous one rather than taken from a clock, so the
 * reducer stays pure. The entry itself is not touched here: the cancellation dialog records the
 * status through its own writes.
 *
 * @param state the notification slice
 */
export function setCancelledReducer(
    state: Readonly<NotificationCollectionState>,
): Readonly<NotificationCollectionState> {
    return { ...state, lastCancellation: (state.lastCancellation ?? 0) + 1 };
}

/**
 * Marks the slice as failed and records why, leaving any previously loaded data — and, through
 * `lastLoad`, the readable state reported by `selectNotificationInitialized` — in place: a failed
 * refresh must not make previously-available notifications disappear.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.error what went wrong, already formatted by the dispatch site
 * @returns the slice marked as failed, carrying the message
 */
export function setNotificationErrorReducer(
    state: Readonly<NotificationCollectionState>,
    props: { error: string },
): Readonly<NotificationCollectionState> {
    return { ...state, loadStatus: LoadStatus.Error, error: props.error };
}

/**
 * Records a failed storage sync. Unlike {@link setNotificationErrorReducer} this leaves `loadStatus` alone: a
 * persist that could not be written, or a cache that could not be restored from, says nothing about
 * the notifications the slice holds — see `NotificationCollectionState.error`.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.error what went wrong, already formatted by the dispatch site
 * @returns the slice carrying the message
 */
export function setStorageSyncErrorReducer(
    state: Readonly<NotificationCollectionState>,
    props: { error: string },
): Readonly<NotificationCollectionState> {
    return { ...state, error: props.error };
}

/**
 * Replaces the full entry collection, re-sorting by notification start. For bulk replacements
 * outside the load flow.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.entries the new entry collection
 * @returns the slice with the new, sorted collection
 */
export function setNotificationEntriesReducer(
    state: Readonly<NotificationCollectionState>,
    props: { entries: NotificationState[] },
): Readonly<NotificationCollectionState> {
    return { ...state, notifications: sortEntriesByStart(props.entries) };
}

/**
 * Records the notification ids the backend sent that failed to parse into a `NotificationModel`.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.ids the ids that failed to parse
 * @returns the slice with the incorrect-id list replaced
 */
export function setIncorrectNotificationsReducer(
    state: Readonly<NotificationCollectionState>,
    props: { ids: string[] },
): Readonly<NotificationCollectionState> {
    return { ...state, incorrectNotificationIds: props.ids };
}

/**
 * Restores the persisted fields from the generic persistence layer. Deliberately never touches
 * `loadStatus` — see the action's doc comment. Restoring a
 * non-empty `entries` array is by itself enough to satisfy `selectNotificationInitialized`, so an
 * offline boot with a warm cache serves notifications immediately instead of rejecting reads until
 * the re-fetch resolves.
 *
 * Entries are completed with the entry defaults, so that a cache written before a field moved onto
 * the entry still restores into a well-formed collection. A cache written before the completion
 * records moved off the entries is restored into the current shape too — see
 * `liftEmbeddedCompletions`.
 *
 * A read that brought no completion map — a cache that predates it, or a read that failed — keeps
 * the map the slice already holds rather than emptying it: losing a completion record would let a
 * submitted confirmation be processed again.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.entries the persisted entries
 * @param props.completions the persisted completion map, `undefined` when the read brought none
 * @returns the slice restored from storage
 */
export function restoredFromStorageReducer(
    state: Readonly<NotificationCollectionState>,
    props: { entries: NotificationState[]; completions: NotificationCompletionsState | undefined },
): Readonly<NotificationCollectionState> {
    const restored = liftEmbeddedCompletions(props.entries, props.completions ?? state.completions);
    return {
        ...state,
        notifications: sortEntriesByStart(restored.entries.map(withEntryDefaults)),
        completions: restored.completions,
    };
}

/**
 * Sets the notification `notificationStatusSignal` tracks. Ephemeral — never touched by
 * `restoredFromStorageReducer`, see the action's doc comment.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification id to track
 * @returns the slice with `currentNotificationId` updated
 */
export function setCurrentNotificationReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState },
): Readonly<NotificationCollectionState> {
    return { ...state, currentNotificationId: props.id };
}

/**
 * Merges `changes` into the notification's status fields — see {@link changeEntry} for the
 * unknown-id case.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification the flags belong to
 * @param props.changes the flags to merge
 * @param props.changes.escalated
 * @param props.changes.cancelled
 * @param props.changes.unread
 * @param props.changes.paused
 * @returns the slice with the entry updated
 */
export function updateStatusFieldsReducer(
    state: Readonly<NotificationCollectionState>,
    props: {
        id: NotificationIdState;
        changes: { escalated?: boolean; cancelled?: string; unread?: boolean; paused?: boolean };
    },
): Readonly<NotificationCollectionState> {
    return changeEntry(state, props.id, (entry) => ({ ...entry, ...props.changes }));
}

/**
 * Replaces the notification's internal-info long-text lines wholesale — see {@link changeEntry} for
 * the unknown-id case.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification the internal info belongs to
 * @param props.internalInfoLines the internal-info lines to store
 * @returns the slice with the entry's internal-info lines replaced
 */
export function updateInternalInfoLinesReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState; internalInfoLines: NotificationLongTextLineState[] },
): Readonly<NotificationCollectionState> {
    return changeEntry(state, props.id, (entry) => ({ ...entry, internalInfoLines: props.internalInfoLines }));
}

/**
 * Appends a status record to the notification. Idempotent: a status code the entry already carries
 * is ignored, so callers do not have to check first.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification to append to
 * @param props.status the status record to append
 * @returns the slice with the status appended, or unchanged when it was already present
 */
export function addStatusToEntryReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState; status: NotificationStatusRecordState },
): Readonly<NotificationCollectionState> {
    return changeEntry(state, props.id, (entry) =>
        entry.status.some((status) => status.statusCode === props.status.statusCode)
            ? entry
            : { ...entry, status: [...entry.status, props.status] },
    );
}

/**
 * Records a travel status the backend accepted, by the rules in `notification-status.rules.ts`:
 * every status the submit produces replaces the one of the same code, so that ETA brings its DURA
 * and OSR moves the TRAV end in the same transition.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification the status is recorded on
 * @param props.submission what the user submitted
 * @returns the slice with the statuses written, or the unchanged slice when there is nothing to write
 */
export function recordTravelStatusReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState; submission: TravelStatusSubmission },
): Readonly<NotificationCollectionState> {
    return changeEntry(state, props.id, (entry) => {
        const records = statusRecordsForTravelStatus(entry, props.submission);
        return records.length === 0 ? entry : { ...entry, status: upsertStatusRecords(entry.status, records) };
    });
}

/**
 * Replaces the status fields of every listed notification; the ones the list does not mention keep
 * what they have.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.entries the status fields to apply, by entry id
 * @returns the slice with the status fields applied
 */
export function setStatusFieldsReducer(
    state: Readonly<NotificationCollectionState>,
    props: { entries: Array<{ id: NotificationIdState; fields: NotificationStatusModelFields }> },
): Readonly<NotificationCollectionState> {
    return props.entries.reduce(
        (current, { id, fields }) => changeEntry(current, id, (entry) => ({ ...entry, ...fields })),
        state,
    );
}

/**
 * Replaces the completion map with the given one — the bulk semantics the legacy list had.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.completions the completion record of each completed notification
 * @returns the slice with the completion map replaced
 */
export function setNotificationCompletionsReducer(
    state: Readonly<NotificationCollectionState>,
    props: { completions: NotificationCompletionsState },
): Readonly<NotificationCollectionState> {
    return { ...state, completions: props.completions };
}

/**
 * Records that a notification's confirmation was completed, whether or not the collection holds
 * the notification.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification the record belongs to
 * @param props.completion the completion record to store
 * @returns the slice with the completion recorded
 */
export function markNotificationCompletedReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState; completion: NotificationCompletionState },
): Readonly<NotificationCollectionState> {
    return {
        ...state,
        completions: {
            ...state.completions,
            [completionKeyOf(props.id)]: { notificationId: props.id, ...props.completion },
        },
    };
}

/**
 * Adds the migrated legacy completion records the map does not already hold. A record already in
 * the map was written by the NgRx path and wins. Returns the unchanged slice when nothing is added.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.completions the records read from the legacy list
 * @returns the slice with the missing records added
 */
export function addLegacyCompletionsReducer(
    state: Readonly<NotificationCollectionState>,
    props: { completions: NotificationCompletionsState },
): Readonly<NotificationCollectionState> {
    const missing = Object.entries(props.completions).filter(([key]) => !(key in state.completions));
    if (missing.length === 0) {
        return state;
    }
    return { ...state, completions: { ...state.completions, ...Object.fromEntries(missing) } };
}

/**
 * Forgets the completion records that have aged out. The cutoff is derived here from
 * {@link COMPLETION_RETENTION_MS} so the retention policy lives in the slice rather than in every
 * caller. Returns the unchanged slice when nothing expired.
 *
 * @param state the notification slice
 * @returns the slice with the expired completion records removed
 */
export function removeExpiredCompletedNotificationsReducer(
    state: Readonly<NotificationCollectionState>,
): Readonly<NotificationCollectionState> {
    const cutoffTimestamp = Date.now() - COMPLETION_RETENTION_MS;
    const kept = Object.entries(state.completions).filter(([, record]) => record.timestampCompleted >= cutoffTimestamp);
    if (kept.length === Object.keys(state.completions).length) {
        return state;
    }
    return { ...state, completions: Object.fromEntries(kept) };
}

/**
 * Records that the user has confirmed a notification's cancellation — see {@link changeEntry} for
 * the unknown-id case.
 *
 * @param state the notification slice
 * @param props the action payload
 * @param props.id the notification whose cancellation was confirmed
 * @returns the slice with the confirmation recorded
 */
export function saveCancellationConfirmationReducer(
    state: Readonly<NotificationCollectionState>,
    props: { id: NotificationIdState },
): Readonly<NotificationCollectionState> {
    return changeEntry(state, props.id, (entry) => ({ ...entry, cancellationConfirmed: true }));
}

/**
 * Clears the cancellation confirmation of every notification that carries one — the whole
 * collection, because the action it serves names no notification (see its doc comment).
 *
 * @param state the notification slice
 */
export function removeCancellationConfirmationReducer(
    state: Readonly<NotificationCollectionState>,
): Readonly<NotificationCollectionState> {
    return changeEveryEntry(state, (entry) =>
        entry.cancellationConfirmed ? { ...entry, cancellationConfirmed: false } : entry,
    );
}

const createNotificationReducer = createReducer(
    initialNotificationCollectionState,
    on(initNotification, setNotificationLoadingReducer),
    on(notificationLoaded, setNotificationLoadedReducer),
    on(notificationFailed, setNotificationErrorReducer),
    on(notificationStorageSyncError, setStorageSyncErrorReducer),
    on(notificationCancelled, setCancelledReducer),
    on(notificationRestoredFromStorage, restoredFromStorageReducer),
    on(setNotificationEntries, setNotificationEntriesReducer),
    on(setIncorrectNotifications, setIncorrectNotificationsReducer),
    on(setCurrentNotification, setCurrentNotificationReducer),
    on(updateNotificationStatusFields, updateStatusFieldsReducer),
    on(updateNotificationInternalInfoLines, updateInternalInfoLinesReducer),
    on(addStatusToNotificationEntry, addStatusToEntryReducer),
    on(travelStatusRecorded, recordTravelStatusReducer),
    on(setNotificationStatusFields, setStatusFieldsReducer),
    on(setNotificationCompletions, setNotificationCompletionsReducer),
    on(markNotificationCompleted, markNotificationCompletedReducer),
    on(removeExpiredCompletedNotifications, removeExpiredCompletedNotificationsReducer),
    on(legacyNotificationCompletionsMigrated, addLegacyCompletionsReducer),
    on(cancellationConfirmationSaved, saveCancellationConfirmationReducer),
    on(cancellationConfirmationRemoved, removeCancellationConfirmationReducer),
);

export function notificationReducer(
    state: Readonly<NotificationCollectionState> | undefined,
    action: Action,
): Readonly<NotificationCollectionState> {
    return createNotificationReducer(state, action);
}
